/**
 * CASTLE ATLAS — Sales & Negotiation Skill Config
 * ─────────────────────────────────────────────────
 * This file owns ALL visual logic for the Sales & Negotiation skill card.
 * Supabase owns content (name, desc, lessons, exercises, colors).
 * This file owns rendering (JSX, animations, card structure).
 *
 * FIXED (this pass): whole-card onClick is now keyboard accessible
 * (role="button", tabIndex, Enter/Space). No visual or prop-interface change.
 *
 * Used by:
 *   - Dashboard.jsx (active/progress card)
 *   - Skills.jsx (preview card)
 *
 * Drop path: frontend/src/data/configs/config.sales-negotiation.js
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/* ── Design tokens ── */
export const TOKENS = {
  stamp:      '#0369a1',
  stampGlow:  'rgba(3,105,161,0.35)',
  trans:      'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
  fast:       'all 0.18s ease',
  radius:     24,
  radiusSm:   8,
  fontSkill:  'var(--font-skill-sales)', // Barlow Condensed
  fontScript: 'var(--font-script, "Caveat", cursive)', // signature CTA — needs Caveat added to your Google Fonts import
};

/* ── Theme-aware helpers ── */
const theme = (isDark) => ({
  paperBg:   isDark
    ? 'linear-gradient(160deg,#1a1814 0%,#1c1a14 50%,#201d16 100%)'
    : 'linear-gradient(160deg,#fafaf8 0%,#f5f2ec 50%,#eee9df 100%)',
  paperBorder: isDark ? '#3a3520' : '#d4c9a8',
  inkColor:    isDark ? '#c8c0a8' : '#2a2418',
  faintInk:    isDark ? 'rgba(200,192,168,0.1)' : 'rgba(42,36,24,0.07)',
  muted:       isDark ? '#78716c' : '#8c8068',
  ctaBg:       isDark ? 'rgba(3,105,161,0.04)' : 'rgba(3,105,161,0.03)',
  ctaBorder:   isDark ? '#2a2820' : '#d4c9a8',
  squareFill:  isDark ? '#2a2820' : '#d4c9a8',
  awaitingColor: isDark ? '#3a3520' : '#c4b89a',
});

/**
 * SalesNegotiationCard
 *
 * Props:
 *   skill         {object}  — row from Supabase skills table
 *   progress      {number}  — exercises completed by this user
 *   lastActivity  {string}  — ISO date string of last exercise completion
 *   isDark        {boolean} — current theme
 *   mode          {string}  — 'dashboard' | 'skills'
 *   onClick       {fn}      — card click handler
 *   onAction      {fn}      — CTA button click
 */
export const SalesNegotiationCard = ({
  skill,
  progress = 0,
  lastActivity,
  isDark = false,
  mode = 'skills',
  onClick,
  onAction,
}) => {
  const [hovered, setHovered] = useState(false);
  const t = theme(isDark);

  const totalExercises = skill?.exercises ?? 30;
  const pct = totalExercises > 0 ? Math.round((progress / totalExercises) * 100) : 0;
  const lastText = lastActivity
    ? new Date(lastActivity).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;

  /* Progress dot count — max 15 visible squares */
  const dotCount  = 15;
  const filledDots = Math.round((progress / totalExercises) * dotCount);

  const wrapperProps = onClick
    ? {
        onClick,
        role: 'button',
        tabIndex: 0,
        onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } },
      }
    : {};

  return (
    <div
      {...wrapperProps}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background:   t.paperBg,
        borderRadius: TOKENS.radius,
        border:       `1px solid ${t.paperBorder}`,
        borderLeft:   '3px solid rgba(3,105,161,0.35)',
        overflow:     'hidden',
        cursor:       onClick ? 'pointer' : 'default',
        position:     'relative',
        transition:   TOKENS.trans,
        transform:    hovered ? 'translateY(-4px) rotate(0deg)' : 'rotate(0.4deg)',
        boxShadow:    hovered
          ? '0 12px 32px rgba(0,0,0,0.08)'
          : (isDark ? '0 4px 16px rgba(0,0,0,0.4)' : '0 4px 16px rgba(42,36,24,0.08)'),
        display:      'flex',
        flexDirection:'column',
      }}
    >
      {/* ── Body ── */}
      <div style={{ padding: '24px 24px 0 22px', position: 'relative', zIndex: 1, flex: 1 }}>

        {/* Header */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', color: TOKENS.stamp, textTransform: 'uppercase', marginBottom: 4, opacity: 0.6 }}>
            Agreement of Skill
          </div>
          <div style={{ fontSize: 20, fontWeight: 900, color: t.inkColor, letterSpacing: '-0.02em', lineHeight: 1.2, marginBottom: 4, fontFamily: TOKENS.fontSkill }}>
            {skill?.name ?? 'Sales & Negotiation'}
          </div>
          <div style={{ fontSize: 11, color: t.muted, fontStyle: 'italic' }}>
            {skill?.tagline ?? 'Close the deal with confidence every single time.'}
          </div>
        </div>

        {/* Contract fields */}
        <div style={{ marginBottom: 14 }}>
          {[
            ['Party',     'Castle Atlas Learner'],
            ['Skill',     skill?.name ?? 'Sales & Negotiation'],
            ['Modules',   `${skill?.lessons ?? 4} modules`],
            ['Exercises', `${progress} of ${totalExercises} completed`],
            ['Progress',  `${pct}%`],
          ].map(([label, value]) => (
            <div key={label} style={{ display: 'flex', gap: 8, marginBottom: 5, alignItems: 'baseline' }}>
              <span style={{ fontSize: 9, color: t.muted, minWidth: 68, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>
                {label}
              </span>
              <div style={{ flex: 1, borderBottom: `1px solid ${t.faintInk}`, paddingBottom: 2 }}>
                <span style={{ fontSize: 12, color: t.inkColor }}>{value}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Square progress dots */}
        <div style={{ marginBottom: 14, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {Array.from({ length: dotCount }).map((_, i) => (
            <div key={i} style={{
              width: 7, height: 7, borderRadius: 0,
              background: i < filledDots ? TOKENS.stamp : t.squareFill,
              transition: 'all 0.3s ease',
            }} />
          ))}
        </div>

        {/* Signature + date */}
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-end', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ borderBottom: `1px solid ${t.faintInk}`, marginBottom: 3, paddingBottom: 4, minHeight: 28, display: 'flex', alignItems: 'flex-end' }}>
              {progress > 0
                ? <svg width="90" height="20" viewBox="0 0 90 20">
                    <path d="M8 16 Q14 8 22 12 T38 10 T52 13 T66 9 T80 12" stroke={TOKENS.stamp} strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.6" />
                  </svg>
                : <span style={{ fontSize: 10, color: t.awaitingColor, fontStyle: 'italic' }}>awaiting signature...</span>}
            </div>
            <div style={{ fontSize: 9, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Learner signature</div>
          </div>
          {lastText && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ borderBottom: `1px solid ${t.faintInk}`, marginBottom: 3, paddingBottom: 2 }}>
                <span style={{ fontSize: 11, color: t.inkColor }}>{lastText}</span>
              </div>
              <div style={{ fontSize: 9, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Date</div>
            </div>
          )}
          {pct >= 100 && (
            <div style={{ border: `2px solid ${TOKENS.stamp}`, padding: '3px 8px', transform: 'rotate(-12deg)', color: TOKENS.stamp, fontSize: 13, fontWeight: 900, letterSpacing: '0.15em', opacity: 0.6 }}>
              CLOSED
            </div>
          )}
        </div>

        {/* Description */}
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 11, color: t.muted, lineHeight: 1.6, margin: 0 }}>{skill?.description}</p>
        </div>
      </div>

      {/* ── CTA — signed, not clicked ── */}
      <div style={{ background: t.ctaBg, borderTop: `1px solid ${t.ctaBorder}`, padding: '10px 24px 18px', position: 'relative', zIndex: 1 }}>
        {onAction ? (
          <button
            onClick={e => { e.stopPropagation(); onAction(); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0 0',
              width: '100%', minHeight: 44, boxSizing: 'border-box',
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontFamily: TOKENS.fontScript, fontSize: 27, color: TOKENS.stamp, lineHeight: 1, letterSpacing: '0.01em' }}>
                {pct > 0 ? `Continue · ${pct}%` : 'Sign & Begin'}
              </span>
              <ArrowRight size={14} color={TOKENS.stamp} style={{ transform: hovered ? 'translateX(3px)' : 'none', transition: TOKENS.fast }} />
            </span>
            <svg width="100%" height="10" viewBox="0 0 220 10" preserveAspectRatio="none" style={{ marginTop: 2 }}>
              <path d="M2,5 Q40,2 68,6 T136,4 T218,6" stroke={TOKENS.stamp} strokeWidth="1.4" fill="none" strokeLinecap="round" opacity={hovered ? 0.9 : 0.45} style={{ transition: 'opacity 0.3s ease' }} />
            </svg>
          </button>
        ) : (
          <Link
            to={skill?.route ?? '/skills/sales-negotiation'}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
              width: '100%', minHeight: 44, boxSizing: 'border-box', padding: '6px 0 0',
              textDecoration: 'none',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontFamily: TOKENS.fontScript, fontSize: 27, color: TOKENS.stamp, lineHeight: 1, letterSpacing: '0.01em' }}>
                {pct > 0 ? `Continue · ${pct}%` : 'Sign & Begin'}
              </span>
              <ArrowRight size={14} color={TOKENS.stamp} style={{ transform: hovered ? 'translateX(3px)' : 'none', transition: TOKENS.fast }} />
            </span>
            <svg width="100%" height="10" viewBox="0 0 220 10" preserveAspectRatio="none" style={{ marginTop: 2 }}>
              <path d="M2,5 Q40,2 68,6 T136,4 T218,6" stroke={TOKENS.stamp} strokeWidth="1.4" fill="none" strokeLinecap="round" opacity={hovered ? 0.9 : 0.45} style={{ transition: 'opacity 0.3s ease' }} />
            </svg>
          </Link>
        )}
      </div>
    </div>
  );
};

export const SHAPE    = 'contract';
export const SKILL_ID = 'sales-negotiation';

export default SalesNegotiationCard;
